import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { Home, MapPin, User as UserIcon, ArrowRight, AlertTriangle, X, UserCheck, Plus, Trash2, Archive, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SeenDot, SeenFilterToggle, isUnseenAssignment } from "../components/SeenIndicator";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { StatusBadge } from "../components/StatusBadge";

// Feb 2026 · Startbild-URL bauen: Backend liefert entweder eine volle URL
// oder eine relative `/api/files/{id}`-Ressource. `API` endet mit `/api`,
// deshalb bei relativen Werten nur den `/files/…`-Rest anhängen.
function resolvePhotoUrl(raw) {
  if (!raw) return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  if (raw.startsWith("/api/")) return `${API.replace(/\/api$/, "")}${raw}`;
  if (raw.startsWith("/")) return `${API}${raw}`;
  return `${API}/files/${raw}`;
}

/**
 * Feb 2026 · Dedizierte „Objekte-Übersicht" für das Team.
 *
 * Alle betreuten Immobilien, sortiert nach Kunde → Objekt.
 *
 * Feb 2026 · Sidebar-Chip-Filter — Query-Parameter `?filter=attention`
 * blendet nur Objekte mit `is_attention=true` ein (Property.status ∈
 * attention/emergency ODER *_severity=attention ODER
 * custom_status.<key>.severity=attention). Wird von der Team-Sidebar
 * via Klick auf die rote Ziffer neben „Objekte" ausgelöst.
 */

const STATUS_PILL = {
  red:   { label: "Handlungsbedarf",     color: STATUS_COLORS.red },
  blue:  { label: "Geplant",             color: STATUS_COLORS.blue },
  green: { label: "Erledigt",            color: STATUS_COLORS.green },
  grey:  { label: "Noch nicht gestartet", color: STATUS_COLORS.grey },
};

// 24 Jun 2026 · „Handlungsbedarf" = rote Kachel-Ampel. Filter, Zähler UND
// Pille teilen EXAKT dieselbe kanonische Definition wie der Backend-Helfer
// `_red_property_ids` / `_property_status_color`: ausschließlich
// `status_color === "red"`. Der frühere `is_attention`-Fallback (rohe
// *_severity-Felder) ist bewusst ENTFERNT — er konnte archivierte/rollup-
// abgestufte Objekte fälschlich als rot zählen (Radhaus-Desync).
const isRedStatus = (r) => (r?.status_color || "grey") === "red";

export default function TeamProperties() {
  const { activeConciergeId, isImpersonatingConcierge, user } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const attentionOnly = searchParams.get("filter") === "attention";
  const unseenOnly = searchParams.get("unseen") === "1";
  const unseenViewer = activeConciergeId || (user?.role === "concierge" ? user?.id : null);
  // Feb 2026 · Directive #300: Deep-Link `?focus={property_id}` aus der
  // Kundenverwaltung / dem Kundenprofil. Scrollt zur passenden Karte und
  // pulst sie 1.5 s golden, damit der Admin sie sofort im Grid findet.
  const focusId = searchParams.get("focus");
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/team/properties");
        if (!cancelled) setRows(data);
      } catch (e) {
        console.error("team/properties load failed:", e);
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Feb 2026 · 9 Sep 2026 · User-Directive „Objektverwaltung":
  // State + Callable-Load für Neue-Objekt- und Archive-Modals.
  const [createOpen, setCreateOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/team/properties");
      setRows(data);
    } catch (e) {
      console.error("team/properties reload failed:", e);
    }
  }, []);

  // 24 Jun 2026 · Desync-Fix „Diese Woche brennt": Der Handlungsbedarf-Zähler
  // (Badge + Grund-Liste) MUSS über exakt dieselbe Grundmenge laufen wie die
  // gerenderte Liste — sonst zeigt der Badge z. B. bei Admin-Impersonation
  // „Handlungsbedarf N", während die Liste (auf den impersonierten Concierge
  // geklemmt) leer bleibt. `scopedRows` ist die Single Source of Truth:
  //   Backend scoped bereits für echte Concierges (Property-, Booking- und
  //   Request-Assignments → `concierge_visible_property_ids`); der Client-
  //   Filter greift NUR bei Admin-Impersonation (Backend liefert dann ALLE).
  const scopedRows = useMemo(() => {
    if (!rows) return [];
    if (isImpersonatingConcierge && activeConciergeId) {
      return rows.filter((r) => r.assigned_concierge_id === activeConciergeId);
    }
    return rows;
  }, [rows, isImpersonatingConcierge, activeConciergeId]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const s = q.trim().toLowerCase();
    let base = attentionOnly ? scopedRows.filter(isRedStatus) : scopedRows;
    if (unseenOnly) base = base.filter((r) => isUnseenAssignment(r, unseenViewer, "assigned_concierge_id"));
    if (!s) return base;
    return base.filter((r) =>
      (r.name || "").toLowerCase().includes(s)
      || (r.address || "").toLowerCase().includes(s)
      || (r.city || "").toLowerCase().includes(s)
      || (r.owner?.name || "").toLowerCase().includes(s)
    );
  }, [rows, scopedRows, q, attentionOnly, unseenOnly, unseenViewer]);

  const attentionRows = useMemo(() => scopedRows.filter(isRedStatus), [scopedRows]);
  const attentionCount = attentionRows.length;
  const unseenCount = useMemo(
    () => scopedRows.filter((r) => isUnseenAssignment(r, unseenViewer, "assigned_concierge_id")).length,
    [scopedRows, unseenViewer],
  );
  const toggleUnseen = () => {
    const next = new URLSearchParams(searchParams);
    if (unseenOnly) next.delete("unseen"); else next.set("unseen", "1");
    setSearchParams(next, { replace: true });
  };

  // Feb 2026 · Directive #301: Kunden mit mehreren Objekten kriegen EINE
  // Karte mit Umschalter (Tabs bei ≤3 Objekten, Dropdown ab 4). So bleibt
  // die Übersicht klar strukturiert, statt für jeden Kunden drei
  // nebeneinanderliegende identisch aussehende Kacheln zu füllen.
  // Reihenfolge im Grid: Owner-first-seen bestimmt die Position.
  const grouped = useMemo(() => {
    if (!filtered) return null;
    const map = new Map();
    for (const p of filtered) {
      const ownerId = p.owner?.id || `__unassigned__${p.id}`;
      if (!map.has(ownerId)) {
        map.set(ownerId, { owner: p.owner || null, properties: [] });
      }
      map.get(ownerId).properties.push(p);
    }
    return Array.from(map.values());
  }, [filtered]);

  const clearFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  };
  const enableFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.set("filter", "attention");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10" data-testid="team-properties-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>Weissblom · Team</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">Objekte</h1>
          <p className="text-[13.5px] text-navy-dark/65 mt-2 max-w-2xl leading-relaxed">
            Alle betreuten Immobilien auf einen Blick — sortiert nach Kunde
            und Objektname. Ein Klick auf den Kundenname öffnet dessen Profil
            in der Kundenverwaltung.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {isAdmin && (
            <div className="flex items-center gap-2" data-testid="team-properties-admin-actions">
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                data-testid="team-properties-new-btn"
                className="inline-flex items-center gap-1.5 text-ivory px-3 py-2 text-[11px] tracking-[0.18em] uppercase hover:opacity-90"
                style={{ background: BRAND_COLORS.navy }}
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                Neues Objekt
              </button>
              <Link
                to="/team/properties/removed"
                data-testid="team-properties-removed-link"
                className="inline-flex items-center gap-1.5 text-navy border border-navy/20 hover:border-gold px-3 py-2 text-[11px] tracking-[0.18em] uppercase"
              >
                <Archive className="w-3.5 h-3.5" strokeWidth={1.8} />
                Entfernte Objekte
              </Link>
            </div>
          )}
          <div className="flex flex-col items-end gap-1">
            <span className="label-caps">Suche</span>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Objekt · Adresse · Kunde"
              className="border border-navy/20 px-3 py-2 text-[13px] w-72"
              data-testid="team-properties-search"
            />
          </div>
        </div>
      </div>

      {/* Feb 2026 · Filter-Chip (Sidebar-Chip-Filter). Sichtbar wenn
          `?filter=attention` gesetzt ist ODER wenn mindestens ein Objekt
          in Handlungsbedarf ist (dann als anklickbare Umschalt-Pille). */}
      {(attentionOnly || attentionCount > 0 || unseenOnly || unseenCount > 0) && (
        <div className="mb-5 flex items-center gap-3 flex-wrap" data-testid="team-properties-filter-bar">
          {attentionOnly ? (
            <button
              type="button"
              onClick={clearFilter}
              data-testid="team-properties-filter-active"
              className="inline-flex items-center gap-2 text-ivory px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full hover:opacity-90 transition-opacity"
              style={{ background: STATUS_COLORS.red }}
              aria-label="Filter zurücksetzen — alle Objekte anzeigen"
            >
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
              Nur Handlungsbedarf · {attentionCount}
              <X className="w-3.5 h-3.5 ml-1" strokeWidth={2} />
            </button>
          ) : (
            <button
              type="button"
              onClick={enableFilter}
              data-testid="team-properties-filter-inactive"
              className="inline-flex items-center gap-2 text-navy px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full border border-navy/25 hover:border-red-attn transition-colors"
              style={{ "--red-attn": STATUS_COLORS.red }}
              aria-label={`Nur Objekte mit Handlungsbedarf anzeigen (${attentionCount})`}
            >
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: STATUS_COLORS.red }} />
              Nur Handlungsbedarf ({attentionCount})
            </button>
          )}
          <SeenFilterToggle active={unseenOnly} count={unseenCount} onToggle={toggleUnseen} testidBase="team-properties-unseen" />
          {attentionOnly && (
            <div
              className="w-full mt-1 text-[12px] text-navy-dark/70"
              data-testid="team-properties-attention-reasons"
            >
              <span className="italic text-navy-dark/55">
                Gefilterte Liste — nur Objekte mit Handlungsbedarf. Grund je Objekt:
              </span>
              <ul className="mt-1.5 space-y-1">
                {attentionRows.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-start gap-1.5"
                    data-testid={`team-properties-attention-reason-${r.id}`}
                  >
                    <AlertTriangle
                      className="w-3 h-3 mt-0.5 shrink-0"
                      style={{ color: STATUS_COLORS.red }}
                      strokeWidth={2}
                    />
                    <span>
                      <span className="font-medium text-navy">{r.name}</span>
                      {" — "}
                      {r.status_reason || "Handlungsbedarf"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {filtered === null ? (
        <div className="text-[13px] text-navy-dark/50 italic" data-testid="team-properties-loading">
          Lade Objekte …
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="p-8 text-center border border-dashed border-navy/20 text-[13px] text-navy-dark/60"
          data-testid="team-properties-empty"
        >
          {attentionOnly
            ? "Aktuell steht kein Objekt auf Handlungsbedarf. Filter zurücksetzen, um alle anzuzeigen."
            : q
              ? `Keine Objekte gefunden für "${q}".`
              : "Noch keine Objekte hinterlegt."}
        </div>
      ) : (
        <>
          <div className="mb-4 text-[12px] text-navy-dark/60 tabular-nums">
            <strong className="text-navy">{filtered.length}</strong>
            {" "}
            {filtered.length === 1 ? "Objekt" : "Objekte"}
            {(q || attentionOnly) ? ` (gefiltert aus ${scopedRows.length})` : ""}
            {grouped && grouped.length !== filtered.length && (
              <span className="text-navy-dark/45">
                {" "}· gebündelt in {grouped.length} {grouped.length === 1 ? "Kunde" : "Kunden"}
              </span>
            )}
          </div>
          <div
            className="space-y-4"
            data-testid="team-properties-grid"
          >
            {grouped.map((group) => (
              <CustomerPropertyRow
                key={group.owner?.id || group.properties[0]?.id}
                group={group}
                focusId={focusId}
                onArchive={setArchiveTarget}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        </>
      )}

      <PropertyCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => { setCreateOpen(false); await reload(); }}
      />
      <PropertyArchiveDialog
        open={!!archiveTarget}
        property={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onArchived={async () => { setArchiveTarget(null); await reload(); }}
      />
    </div>
  );
}


function CustomerPropertyRow({ group, focusId, onArchive, isAdmin }) {
  const { owner, properties } = group;
  // Feb 2026 · Directive #305: Kundenname + Info sitzen als schmale Kopfzeile
  // ÜBER den Kacheln (nicht mehr links daneben). Die Zeile fasst die
  // darunterliegenden Objekt-Kacheln optisch zusammen.
  return (
    <article
      data-testid={`team-property-row-${owner?.id || properties[0]?.id}`}
      className="bg-white border border-navy/10"
    >
      <header
        className="flex items-baseline gap-3 flex-wrap px-4 md:px-5 py-2.5 border-b border-navy/10 bg-ivory/40"
        data-testid={`team-property-row-header-${owner?.id || properties[0]?.id}`}
      >
        {owner?.id ? (
          <Link
            to={`/team/customers/${owner.id}`}
            data-testid={`team-property-row-owner-${owner.id}`}
            className="group inline-flex items-baseline gap-1.5 text-navy hover:text-gold transition-colors min-w-0"
            title="Zum Kundenprofil"
          >
            <span className="font-display text-lg leading-tight truncate">
              {owner.name || "Kunde"}
            </span>
            <ArrowRight
              className="w-3 h-3 shrink-0 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"
              strokeWidth={2}
            />
          </Link>
        ) : (
          <span className="font-display text-lg text-navy-dark/50 italic">
            — kein Kunde —
          </span>
        )}
        {owner?.email && (
          <span className="text-[11.5px] text-navy-dark/55 truncate">
            {owner.email}
          </span>
        )}
        <span
          className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[10.5px] tracking-[0.14em] uppercase text-navy-dark/75 bg-white border border-navy/10 shrink-0"
          data-testid={`team-property-row-count-${owner?.id || properties[0]?.id}`}
        >
          <Home className="w-3 h-3" strokeWidth={1.6} />
          {properties.length} {properties.length === 1 ? "Objekt" : "Objekte"}
        </span>
      </header>
      <div className="p-4 md:p-5">
        <div className="flex flex-wrap gap-4">
          {properties.map((p) => (
            <PropertyCard key={p.id} p={p} isFocused={focusId === p.id} onArchive={onArchive} isAdmin={isAdmin} />
          ))}
        </div>
      </div>
    </article>
  );
}


function PropertyCard({ p, isFocused, onArchive, isAdmin }) {
  const cardRef = useRef(null);
  const { activeConciergeId } = useAuth();
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (!isFocused || !cardRef.current) return;
    // Nach Mount + Grid-Layout einmal scrollen + kurz golden aufblitzen
    const t = setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setPulse(true);
      setTimeout(() => setPulse(false), 1800);
    }, 150);
    return () => clearTimeout(t);
  }, [isFocused]);
  // Feb 2026 · HARDCODED-RULE Ankunft-Latch-Kopplung:
  // Die Pille zeigt EIN einziges Signal — entweder „Handlungsbedarf" (rot)
  // wenn mindestens eine der 6 LOCKED-Kacheln auf attention steht ODER das
  // Team `property.status` manuell auf attention/emergency gesetzt hat,
  // sonst das reguläre Status-Label. Kein Doppel-Label mehr — spiegelt
  // exakt dieselbe Logik wie die Timeline-Ankunft-Phase.
  // 24 Jun 2026 · Objekt-Ampel: die Kachel-Pille spiegelt den vom Backend
  // gerollten Objekt-Status (`status_color`) — ROT Handlungsbedarf, BLAU
  // geplant, GRÜN alles erledigt, GRAU neutral. Fallback auf is_attention,
  // falls das Backend (offline-Cache) noch kein status_color liefert.
  const colorKey = p.status_color || "grey";
  const pill = STATUS_PILL[colorKey] || STATUS_PILL.grey;
  const showAttention = colorKey === "red";
  const statusLabel = pill.label;
  const statusColor = pill.color;
  const photoSrc = resolvePhotoUrl(p.photo_url);
  // Feb 2026 · Directive #303: Klick auf die Karte öffnet nicht mehr den
  // Kunden-Profil-Live-Status-Anker, sondern die dedizierte Objekt-Detail-
  // seite (`/team/properties/{id}`) mit allen zuvor aus dem Kundenprofil
  // ausgelagerten Sektionen (Timeline, Checkliste, Zusatzleistungen,
  // Live-Status, Anfragen, Einzelbuchungen).
  const targetHref = `/team/properties/${p.id}`;

  const cardInner = (
    <>
      <div
        className="h-40 w-full bg-navy/5 flex items-center justify-center overflow-hidden relative"
        aria-hidden={photoSrc ? undefined : "true"}
      >
        <span className="absolute top-2 left-2 z-10">
          <SeenDot entity={p} viewerId={activeConciergeId} assigneeField="assigned_concierge_id" testid={`team-property-seen-${p.id}`} />
        </span>
        {photoSrc ? (
          <img
            src={photoSrc}
            alt={p.name}
            className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
            loading="lazy"
            data-testid={`team-property-photo-${p.id}`}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-navy-dark/40">
            <Home className="w-8 h-8" strokeWidth={1.2} />
            <span className="label-caps text-navy-dark/40">Kein Bild</span>
          </div>
        )}
        <StatusBadge
          colorKey={colorKey}
          label={statusLabel}
          testid={`team-property-status-${p.id}`}
          title={p.status_reason || statusLabel}
          className="absolute top-2 right-2 shadow-sm"
        />
        {isAdmin && onArchive && (
          <button
            type="button"
            data-testid={`team-property-archive-btn-${p.id}`}
            title="Objekt entfernen (PIN erforderlich)"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onArchive(p);
            }}
            className="absolute top-2 left-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/85 hover:bg-white text-navy-dark hover:text-red-600 shadow-sm transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
          </button>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <h3
          className="font-display text-xl text-navy leading-tight mb-1 group-hover:text-gold transition-colors"
          data-testid={`team-property-name-${p.id}`}
        >
          {p.name}
        </h3>
        <div className="flex items-start gap-1.5 text-[12.5px] text-navy-dark/70 leading-relaxed">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-navy-dark/40" strokeWidth={1.6} />
          <div>
            <div data-testid={`team-property-address-${p.id}`}>{p.address}</div>
            {p.city && <div className="text-navy-dark/55">{p.city}</div>}
          </div>
        </div>

        {/* Feb 2026 · Verantwortlicher Concierge — nur sichtbar wenn
            zugewiesen. Der Admin ändert die Zuweisung im Kundenprofil
            (`/team/customers/{id}` → Property-Sektion). */}
        {p.assigned_concierge_name ? (
          <div
            className="mt-2 flex items-center gap-1.5 text-[12px] text-navy-dark/70"
            data-testid={`team-property-concierge-${p.id}`}
          >
            <UserCheck className="w-3.5 h-3.5 shrink-0 text-navy-dark/40" strokeWidth={1.6} />
            <span>
              <span className="label-caps text-[10.5px] text-navy-dark/50">Concierge · </span>
              {p.assigned_concierge_name}
            </span>
          </div>
        ) : (
          // Feb 2026 · User-Directive „Property-Warnung": Objekte ohne
          // zugewiesenen Concierge lassen sich weder mit Grau→Blau-Regel
          // durch Vorbereitung schieben noch bekommen sie automatisch
          // Booking-Assignees. Der Admin muss diese Lücke schließen.
          <div
            className="mt-2 flex items-center gap-1.5 text-[11.5px]"
            data-testid={`team-property-no-concierge-${p.id}`}
            style={{ color: STATUS_COLORS.red }}
          >
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />
            <span className="tracking-[0.06em] font-medium">
              Kein Concierge zugewiesen
            </span>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-navy/10 flex items-center justify-between gap-2">
          {p.owner?.id ? (
            <span
              className="inline-flex items-center gap-2 text-[12.5px] text-navy font-medium min-w-0"
              data-testid={`team-property-owner-${p.id}`}
            >
              <UserIcon className="w-3.5 h-3.5 shrink-0 text-navy-dark/40" strokeWidth={1.8} />
              <span className="truncate">{p.owner.name}</span>
              <ArrowRight
                className="w-3.5 h-3.5 shrink-0 text-navy-dark/40 group-hover:text-gold group-hover:translate-x-0.5 transition-all"
                strokeWidth={2}
              />
            </span>
          ) : (
            <span className="text-[12.5px] text-navy-dark/50 italic">— kein Kunde zugeordnet —</span>
          )}
        </div>
      </div>
    </>
  );

  if (targetHref) {
    return (
      <Link
        to={targetHref}
        ref={cardRef}
        data-testid={`team-property-card-${p.id}`}
        data-focused={isFocused ? "true" : undefined}
        title={p.name ? `Zur Detailansicht von ${p.name}` : undefined}
        className="group bg-white border flex flex-col overflow-hidden transition-all hover:border-gold hover:shadow-md focus:outline-none focus:border-gold w-full sm:basis-[300px] sm:grow-0 sm:shrink-0 md:basis-[280px]"
        style={{
          borderColor: pulse ? BRAND_COLORS.gold : "rgba(11,42,64,0.10)",
          boxShadow: pulse ? `0 0 0 3px ${BRAND_COLORS.gold}` : undefined,
        }}
      >
        {cardInner}
      </Link>
    );
  }
  return (
    <article
      ref={cardRef}
      data-testid={`team-property-card-${p.id}`}
      data-focused={isFocused ? "true" : undefined}
      className="bg-white border flex flex-col overflow-hidden transition-all w-full sm:basis-[300px] sm:grow-0 sm:shrink-0 md:basis-[280px]"
      style={{
        borderColor: pulse ? BRAND_COLORS.gold : "rgba(11,42,64,0.10)",
        boxShadow: pulse ? `0 0 0 3px ${BRAND_COLORS.gold}` : undefined,
      }}
    >
      {cardInner}
    </article>
  );
}


// ---------- Feb 2026 · 9 Sep 2026 · Neues-Objekt-Dialog (Admin only) ----------
function PropertyCreateDialog({ open, onClose, onCreated }) {
  const [customers, setCustomers] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setCustomerId(""); setName(""); setAddress(""); setZip(""); setCity(""); setCountry(""); return; }
    (async () => {
      try {
        const { data } = await api.get("/team/customers");
        // Nur echte Kunden (nicht Guests) — Backend filtert bereits
        setCustomers(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("customer load failed:", e);
        setCustomers([]);
      }
    })();
  }, [open]);

  if (!open) return null;

  // Feb 2026 · 9 Sep 2026 · User-Directive „Postleitzahl Pflichtfeld":
  // Submit ist erst möglich, wenn Kunde, Name, Adresse UND PLZ gefüllt sind.
  // Zusatz „PLZ-Format-Check" — genau 4-5 Ziffern (DE 5, CH/AT 4). Verhindert
  // dass der Admin versehentlich Telefonnummern oder Straßen als PLZ speichert.
  const zipTrim = zip.trim();
  const zipValid = /^\d{4,5}$/.test(zipTrim);
  const canSubmit = customerId && name.trim() && address.trim() && zipValid && !busy;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/team/properties", {
        customer_id: customerId,
        name: name.trim(),
        address: address.trim(),
        zip: zip.trim(),
        city: city.trim() || undefined,
        country: country.trim() || undefined,
      });
      toast.success(`Objekt „${name}" angelegt.`);
      onCreated?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(13,25,48,0.6)" }}
      onClick={onClose}
      data-testid="property-create-modal"
    >
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        className="bg-white w-full max-w-lg relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
          aria-label="Schliessen"
          data-testid="property-create-close"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <div className="p-6 md:p-8">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
          >
            <Plus className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <h2 className="font-display text-2xl text-navy leading-tight mb-1">
            Neues Objekt anlegen
          </h2>
          <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-5">
            Jedes Objekt muss an einen Kunden gekoppelt sein. Der Kunde
            sieht das neue Objekt sofort in seiner App.
          </p>

          <div className="space-y-3 mb-5">
            <div>
              <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
                Kunde *
              </label>
              <select
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                required
                data-testid="property-create-customer"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
              >
                <option value="">— Kunde wählen —</option>
                {(customers || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.email} · {c.email}
                  </option>
                ))}
              </select>
              {customers === null && (
                <div className="text-[11px] text-navy-dark/50 italic mt-1">Lade Kunden …</div>
              )}
            </div>
            <div>
              <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
                Objektname *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="property-create-name"
                placeholder="z.B. Villa Alpenblick"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
                Adresse *
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
                data-testid="property-create-address"
                placeholder="Straße + Hausnummer"
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
                  Postleitzahl *
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{4,5}"
                  maxLength={5}
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  required
                  data-testid="property-create-zip"
                  placeholder="z.B. 10115"
                  className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy tabular-nums"
                  style={zipTrim && !zipValid ? { borderColor: STATUS_COLORS.red } : undefined}
                />
                {zipTrim && !zipValid && (
                  <p
                    data-testid="property-create-zip-error"
                    className="text-[10.5px] mt-1"
                    style={{ color: STATUS_COLORS.red }}
                  >
                    4 oder 5 Ziffern (z.B. 10115 oder 5700).
                  </p>
                )}
              </div>
              <div>
                <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
                  Stadt (optional)
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  data-testid="property-create-city"
                  className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
                />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
                  Land (optional)
                </label>
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="property-create-country"
                  className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
              data-testid="property-create-cancel"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="property-create-submit"
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
              style={{ background: BRAND_COLORS.navy }}
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
              Objekt anlegen
            </button>
          </div>
        </div>
      </form>
    </div>,
    document.body,
  );
}


// ---------- Feb 2026 · 9 Sep 2026 · Archive-Objekt-Dialog (PIN-Gated) ----------
function PropertyArchiveDialog({ open, property, onClose, onArchived }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setPin(""); setBusy(false); }
  }, [open]);

  if (!open || !property) return null;

  const submit = async () => {
    if (!/^\d{4}$/.test(pin)) {
      toast.error("Bitte 4 Ziffern eingeben.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/team/properties/${property.id}/archive`, { admin_pin: pin });
      toast.success(`Objekt „${property.name}" entfernt.`);
      onArchived?.();
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) toast.error("PIN ist falsch. Bitte erneut eingeben.");
      else toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(13,25,48,0.6)" }}
      onClick={onClose}
      data-testid="property-archive-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
          aria-label="Schliessen"
          data-testid="property-archive-close"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <div className="p-6 md:p-8">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
            style={{ background: withAlpha(STATUS_COLORS.red, 0.15), color: STATUS_COLORS.red }}
          >
            <Trash2 className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <h2 className="font-display text-2xl text-navy leading-tight mb-1">
            Objekt entfernen?
          </h2>
          <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-5">
            <b>{property.name}</b> wird in den Bereich „Entfernte Objekte"
            verschoben. Alle Buchungen, Anfragen, Rechnungen und Dokumente
            bleiben erhalten. Du kannst das Objekt jederzeit wiederherstellen.
          </p>

          <div className="mb-4">
            <label className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 block mb-1.5">
              Bearbeitungs-PIN
            </label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
              placeholder="4 Ziffern"
              data-testid="property-archive-pin"
              className="w-32 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[14px] tabular-nums tracking-widest text-navy"
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
              data-testid="property-archive-cancel"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || pin.length !== 4}
              data-testid="property-archive-confirm"
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
              style={{ background: STATUS_COLORS.red }}
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
              <KeyRound className="w-3.5 h-3.5" strokeWidth={1.8} />
              Entfernen
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
